import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { dirFor } from './locale.js';

import enCommon from '@/locales/en/common.json';
import enNav from '@/locales/en/nav.json';
import enAuth from '@/locales/en/auth.json';
import enTreasury from '@/locales/en/treasury.json';
import enAccounting from '@/locales/en/accounting.json';
import enReports from '@/locales/en/reports.json';
import enDocuments from '@/locales/en/documents.json';
import enCommissions from '@/locales/en/commissions.json';
import enBrokers from '@/locales/en/brokers.json';
import enAdmin from '@/locales/en/admin.json';
import enPayroll from '@/locales/en/payroll.json';
import enProperties from '@/locales/en/properties.json';
import enCustomers from '@/locales/en/customers.json';
import enOnboarding from '@/locales/en/onboarding.json';
import enAgency from '@/locales/en/agency.json';
import enCleaner from '@/locales/en/cleaner.json';

import arCommon from '@/locales/ar/common.json';
import arNav from '@/locales/ar/nav.json';
import arAuth from '@/locales/ar/auth.json';
import arTreasury from '@/locales/ar/treasury.json';
import arAccounting from '@/locales/ar/accounting.json';
import arReports from '@/locales/ar/reports.json';
import arDocuments from '@/locales/ar/documents.json';
import arCommissions from '@/locales/ar/commissions.json';
import arBrokers from '@/locales/ar/brokers.json';
import arAdmin from '@/locales/ar/admin.json';
import arPayroll from '@/locales/ar/payroll.json';
import arProperties from '@/locales/ar/properties.json';
import arCustomers from '@/locales/ar/customers.json';
import arOnboarding from '@/locales/ar/onboarding.json';
import arAgency from '@/locales/ar/agency.json';
import arCleaner from '@/locales/ar/cleaner.json';

import ckbCommon from '@/locales/ckb/common.json';
import ckbNav from '@/locales/ckb/nav.json';
import ckbAuth from '@/locales/ckb/auth.json';
import ckbTreasury from '@/locales/ckb/treasury.json';
import ckbAccounting from '@/locales/ckb/accounting.json';
import ckbReports from '@/locales/ckb/reports.json';
import ckbDocuments from '@/locales/ckb/documents.json';
import ckbCommissions from '@/locales/ckb/commissions.json';
import ckbBrokers from '@/locales/ckb/brokers.json';
import ckbAdmin from '@/locales/ckb/admin.json';
import ckbPayroll from '@/locales/ckb/payroll.json';
import ckbProperties from '@/locales/ckb/properties.json';
import ckbCustomers from '@/locales/ckb/customers.json';
import ckbOnboarding from '@/locales/ckb/onboarding.json';
import ckbAgency from '@/locales/ckb/agency.json';
import ckbCleaner from '@/locales/ckb/cleaner.json';

/**
 * Namespaces are per feature (docs/04 §5). Both locale files must be updated in the same
 * commit as the feature — a missing Arabic key renders the raw key on screen, which is
 * how half-translated screens ship unnoticed.
 */
export const resources = {
  en: {
    common: enCommon,
    nav: enNav,
    auth: enAuth,
    treasury: enTreasury,
    accounting: enAccounting,
    reports: enReports,
    documents: enDocuments,
    commissions: enCommissions,
    brokers: enBrokers,
    admin: enAdmin,
    payroll: enPayroll,
    properties: enProperties,
    customers: enCustomers,
    onboarding: enOnboarding,
    agency: enAgency,
    cleaner: enCleaner,
  },
  ar: {
    common: arCommon,
    nav: arNav,
    auth: arAuth,
    treasury: arTreasury,
    accounting: arAccounting,
    reports: arReports,
    documents: arDocuments,
    commissions: arCommissions,
    brokers: arBrokers,
    admin: arAdmin,
    payroll: arPayroll,
    properties: arProperties,
    customers: arCustomers,
    onboarding: arOnboarding,
    agency: arAgency,
    cleaner: arCleaner,
  },
  ckb: {
    common: ckbCommon,
    nav: ckbNav,
    auth: ckbAuth,
    treasury: ckbTreasury,
    accounting: ckbAccounting,
    reports: ckbReports,
    documents: ckbDocuments,
    commissions: ckbCommissions,
    brokers: ckbBrokers,
    admin: ckbAdmin,
    payroll: ckbPayroll,
    properties: ckbProperties,
    customers: ckbCustomers,
    onboarding: ckbOnboarding,
    agency: ckbAgency,
    cleaner: ckbCleaner,
  },
};

/** `label` is each language's own name for itself — never a translation of it. */
export const SUPPORTED_LOCALES = [
  { code: 'en', label: 'English', dir: 'ltr' },
  { code: 'ar', label: 'العربية', dir: 'rtl' },
  { code: 'ckb', label: 'کوردیی ناوەندی', dir: 'rtl' },
];

export { dirFor };

i18n.use(initReactI18next).init({
  resources,
  lng: 'en',
  fallbackLng: 'en',
  defaultNS: 'common',
  ns: [
    'common',
    'nav',
    'auth',
    'treasury',
    'accounting',
    'reports',
    'documents',
    'commissions',
    'brokers',
    'admin',
    'payroll',
    'properties',
    'customers',
    'onboarding',
  ],
  interpolation: {
    escapeValue: false, // React already escapes
  },
  returnEmptyString: false,
});

/** Applies the locale to i18next *and* to `<html lang dir>` — docs/04 §5. */
export const applyLocale = (locale) => {
  const next = SUPPORTED_LOCALES.some((l) => l.code === locale) ? locale : 'en';
  if (i18n.language !== next) i18n.changeLanguage(next);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = next;
    document.documentElement.dir = dirFor(next);
  }
  return next;
};

export default i18n;
